import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { getTrendingLocutores, getTopLocutores, allLocutores, sistemaLocutoresStats, sistemaLocutoresReglas } from '@/mocks/sistemaLocutores';
import type { LocutorPerfil } from '@/mocks/sistemaLocutores';

const sortOptions = [
  { key: 'trending', label: 'Tendencia', icon: 'ri-fire-line' },
  { key: 'top', label: 'Top Ranking', icon: 'ri-trophy-line' },
  { key: 'followers', label: 'Más Seguidos', icon: 'ri-group-line' },
  { key: 'engagement', label: 'Más Engagement', icon: 'ri-bar-chart-line' },
  { key: 'onair', label: 'Al Aire', icon: 'ri-broadcast-line' },
] as const;
type SortKey = (typeof sortOptions)[number]['key'];

export default function SistemaLocutoresPage() {
  const [sortMode, setSortMode] = useState<SortKey>('trending');
  const [activeView, setActiveView] = useState<'grid' | 'reglas'>('grid');

  const sorted = useMemo(() => {
    const list = [...allLocutores];
    if (sortMode === 'trending') {
      return list.sort((a, b) => a.rankingPosition - b.rankingPosition);
    }
    if (sortMode === 'top') {
      return list.sort((a, b) => a.rankingPosition - b.rankingPosition);
    }
    if (sortMode === 'followers') {
      return list.sort((a, b) => b.followers - a.followers);
    }
    if (sortMode === 'engagement') {
      return list.sort((a, b) => b.stats.engagementRate - a.stats.engagementRate);
    }
    if (sortMode === 'onair') {
      return list.sort((a, b) => {
        if (a.status === 'on_air' && b.status !== 'on_air') return -1;
        if (a.status !== 'on_air' && b.status === 'on_air') return 1;
        return a.rankingPosition - b.rankingPosition;
      });
    }
    return list;
  }, [sortMode]);

  const trending = getTrendingLocutores();
  const top = getTopLocutores();

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      {/* HERO */}
      <section className="relative w-full h-[360px] md:h-[480px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20radio%20broadcasters%20group%20portrait%2C%20diverse%20Latino%20hosts%20and%20DJs%20in%20modern%20studio%2C%20warm%20dramatic%20lighting%2C%20confident%20charismatic%20expressions%2C%20creative%20media%20environment%2C%20editorial%20photography%20capturing%20radio%20talent%20energy%2C%20professional%20atmosphere%20with%20microphones%20and%20headphones%20visible%2C%20wide%20cinematic%20composition%20with%20depth&width=1600&height=900&seq=sistema-locutores-hero-01&orientation=landscape"
          alt="Sistema de Locutores RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="flex items-center gap-2 mb-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500"></span>
            </span>
            <span className="text-[10px] font-extrabold text-background-50 tracking-widest uppercase bg-rose-500/80 px-3 py-1 rounded-full backdrop-blur-sm">
              SISTEMA DE LOCUTORES
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Las Voces que Hacen Radio
          </h1>
          <p className="text-sm md:text-base text-background-50/75 max-w-xl leading-relaxed">
            Descubrí a los locutores que están marcando tendencia. Seguilos, compartí su contenido e invitalos a tu club. Ellos son las estrellas de la plataforma.
          </p>
          <div className="flex items-center gap-5 mt-5 flex-wrap justify-center">
            {[
              { value: sistemaLocutoresStats.totalLocutores.toLocaleString(), label: 'Locutores Totales' },
              { value: sistemaLocutoresStats.onAirNow.toString(), label: 'Al Aire Ahora' },
              { value: sistemaLocutoresStats.verifiedCount.toString(), label: 'Verificados' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <span className="text-2xl font-extrabold text-background-50 leading-none block">{stat.value}</span>
                <span className="text-[10px] text-background-50/60 uppercase tracking-wider">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Row */}
      <section className="bg-background-100 py-5">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex flex-wrap gap-5 items-center justify-center">
          {[
            { icon: 'ri-user-voice-line', label: 'Locutores', value: sistemaLocutoresStats.totalLocutores.toLocaleString() },
            { icon: 'ri-fire-line', label: 'En Tendencia', value: sistemaLocutoresStats.trendingCount.toString() },
            { icon: 'ri-trophy-line', label: 'Top Locutores', value: sistemaLocutoresStats.topCount.toString() },
            { icon: 'ri-broadcast-line', label: 'Al Aire', value: sistemaLocutoresStats.onAirNow.toString() },
            { icon: 'ri-group-line', label: 'Seguidores Totales', value: `${(sistemaLocutoresStats.totalFollowers / 1000000).toFixed(1)}M` },
            { icon: 'ri-radio-line', label: 'Radios', value: sistemaLocutoresStats.totalRadios.toString() },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 px-4">
              <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center">
                <i className={`${stat.icon} text-base text-foreground-500`}></i>
              </div>
              <div>
                <p className="text-lg font-extrabold text-foreground-950 leading-none">{stat.value}</p>
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Controls */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 pt-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          {/* View toggle */}
          <div className="flex items-center bg-background-100 rounded-full p-1 text-xs">
            <button
              onClick={() => setActiveView('grid')}
              className={`px-4 py-2 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeView === 'grid' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-grid-line"></i> Locutores
            </button>
            <button
              onClick={() => setActiveView('reglas')}
              className={`px-4 py-2 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeView === 'reglas' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-scales-line"></i> Reglas
            </button>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-1 flex-wrap">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortMode(opt.key)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                  sortMode === opt.key
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-500 hover:text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={`${opt.icon} text-xs`}></i>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Trending Strip */}
        {sortMode === 'trending' && activeView === 'grid' && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
                <i className="ri-fire-line text-rose-600 text-sm"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950">En Tendencia Ahora</h2>
              <span className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-full">{trending.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {trending.map((locutor) => (
                <LocutorCard key={locutor.id} locutor={locutor} isTrending />
              ))}
            </div>
          </div>
        )}

        {/* Top Strip */}
        {sortMode === 'top' && activeView === 'grid' && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <i className="ri-trophy-line text-amber-600 text-sm"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950">Top Locutores</h2>
              <span className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-full">{top.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {top.map((locutor, i) => (
                <LocutorCard key={locutor.id} locutor={locutor} rank={i + 1} />
              ))}
            </div>
          </div>
        )}

        {/* Full Grid */}
        {activeView === 'grid' && (sortMode === 'followers' || sortMode === 'engagement' || sortMode === 'onair') && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <i className={`${sortOptions.find((o) => o.key === sortMode)?.icon} text-emerald-600 text-sm`}></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950">
                {sortOptions.find((o) => o.key === sortMode)?.label}
              </h2>
              <span className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-full">{sorted.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {sorted.map((locutor, i) => (
                <LocutorCard key={locutor.id} locutor={locutor} rank={i + 1} />
              ))}
            </div>
          </div>
        )}

        {/* Reglas View */}
        {activeView === 'reglas' && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                <i className="ri-scales-line text-sky-600 text-sm"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950">Reglas del Sistema</h2>
              <span className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-full">{sistemaLocutoresReglas.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {sistemaLocutoresReglas.map((regla) => (
                <div key={regla.id} className="bg-white rounded-xl border border-foreground-100 p-6 flex items-start gap-4 group hover:shadow-sm transition-all">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${regla.color} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${regla.icon} text-white text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground-950 text-base leading-snug">{regla.titulo}</h3>
                    <p className="text-sm text-foreground-500 mt-2 leading-relaxed">{regla.descripcion}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Stats summary */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Locutores Totales', value: sistemaLocutoresStats.totalLocutores.toLocaleString() },
                { label: 'Seguidores Totales', value: `${(sistemaLocutoresStats.totalFollowers / 1000000).toFixed(1)}M` },
                { label: 'Engagement Promedio', value: `${sistemaLocutoresStats.avgEngagementRate}%` },
                { label: 'Radios con Locutores', value: sistemaLocutoresStats.totalRadios.toString() },
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-xl border border-foreground-100 p-4 text-center">
                  <span className="text-xl font-extrabold text-foreground-950 block">{stat.value}</span>
                  <span className="text-[10px] text-foreground-400 uppercase tracking-wider">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* CTA: ¿Sos locutor? */}
      <section className="py-10">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="bg-gradient-to-r from-rose-500 to-amber-500 rounded-2xl p-8 md:p-10 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">¿Sos locutor y querés tu perfil?</h2>
            <p className="text-white/80 text-sm max-w-lg mx-auto mb-6 leading-relaxed">
              Registrá tu radio en RadioFrecuenciaWeb y automáticamente tendrás tu perfil de locutor con foto, portada, biografía, redes sociales, programas y ranking. Todo lo que necesitás para brillar.
            </p>
            <Link
              to="/register-radio"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-rose-600 font-bold rounded-xl hover:bg-rose-50 transition-all cursor-pointer text-sm"
            >
              <i className="ri-mic-line"></i>
              Crear mi perfil de locutor
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function LocutorCard({ locutor, rank, isTrending }: { locutor: LocutorPerfil; rank?: number; isTrending?: boolean }) {
  const activePrograms = locutor.activePrograms.filter((p) => p.active);
  const pastPrograms = locutor.activePrograms.filter((p) => !p.active);

  return (
    <Link
      to={`/host/${locutor.slug}`}
      className="group bg-white rounded-xl border border-foreground-100 overflow-hidden hover:shadow-sm transition-all cursor-pointer flex flex-col"
    >
      {/* Cover */}
      <div className="relative w-full h-32 md:h-40 overflow-hidden">
        <img src={locutor.coverUrl} alt={locutor.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

        {/* Rank badge */}
        {rank && (
          <div className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-extrabold px-2.5 py-1 rounded-full">
            #{rank}
          </div>
        )}

        {/* Trending badge */}
        {isTrending && (
          <div className="absolute top-3 left-3 bg-rose-500 text-white text-[10px] font-extrabold px-2.5 py-1 rounded-full flex items-center gap-1">
            <i className="ri-fire-line text-xs"></i>
            TENDENCIA
          </div>
        )}

        {/* Status */}
        <div className="absolute top-3 right-3">
          {locutor.status === 'on_air' ? (
            <span className="bg-red-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              EN VIVO
            </span>
          ) : (
            <span className="bg-emerald-500/90 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
              ACTIVO
            </span>
          )}
        </div>

        {/* Avatar overlay */}
        <div className="absolute -bottom-8 left-4">
          <div className="w-16 h-16 rounded-full border-3 border-white overflow-hidden bg-background-50">
            <img src={locutor.photoUrl} alt={locutor.name} className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 pt-10">
        <div className="flex items-center gap-1.5">
          <h3 className="font-bold text-foreground-950 text-sm group-hover:text-accent-600 transition-colors">{locutor.name}</h3>
          {locutor.verified && (
            <span className="w-4 h-4 flex items-center justify-center rounded-full bg-accent-500 text-background-50 flex-shrink-0">
              <i className="ri-verified-badge-fill text-[10px]"></i>
            </span>
          )}
        </div>
        <p className="text-xs text-foreground-500 mt-0.5">{locutor.username}</p>

        {/* Stats */}
        <div className="flex items-center gap-3 mt-3 text-[10px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-group-line"></i>
            {locutor.followers >= 1000 ? `${(locutor.followers / 1000).toFixed(1)}K` : locutor.followers}
          </span>
          <span>·</span>
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line"></i>
            {locutor.city}
          </span>
          <span>·</span>
          <span className="flex items-center gap-1 text-amber-600 font-medium">
            <i className="ri-trophy-line"></i>
            #{locutor.rankingPosition}
          </span>
        </div>

        {/* Programs */}
        <div className="mt-3 space-y-1.5">
          {activePrograms.slice(0, 2).map((prog) => (
            <div key={prog.id} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></div>
              <span className="text-[10px] text-foreground-600 truncate">{prog.name}</span>
              <span className="text-[9px] text-foreground-400 ml-auto whitespace-nowrap">{prog.schedule}</span>
            </div>
          ))}
          {pastPrograms.length > 0 && (
            <div className="flex items-center gap-2 opacity-60">
              <div className="w-1.5 h-1.5 rounded-full bg-foreground-300 flex-shrink-0"></div>
              <span className="text-[10px] text-foreground-400 truncate">{pastPrograms[0].name}</span>
              <span className="text-[9px] text-foreground-400 ml-auto whitespace-nowrap">Finalizado</span>
            </div>
          )}
        </div>

        {/* Social Icons */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-foreground-100">
          {locutor.socials.slice(0, 4).map((social, i) => (
            <div key={i} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center text-foreground-400 hover:text-accent-500 transition-colors cursor-pointer" title={social.platform}>
              <i className={`${social.icon} text-xs`}></i>
            </div>
          ))}
          {/* Badges count */}
          <div className="ml-auto flex items-center gap-1 text-[10px] text-foreground-400">
            <i className="ri-medal-line"></i>
            <span>{locutor.badges.filter((b) => b.earned).length}</span>
          </div>
        </div>

        {/* Growth indicator */}
        <div className="mt-2 flex items-center gap-1.5">
          <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${locutor.followersGrowth > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            <i className={`${locutor.followersGrowth > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-[10px]`}></i>
            {locutor.followersGrowth > 0 ? '+' : ''}{locutor.followersGrowth}%
          </span>
          <span className="text-[10px] text-foreground-400">esta semana</span>
        </div>
      </div>
    </Link>
  );
}